import { Outlet, NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useShiftFreeze } from "@/context/ShiftFreezeContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import ShiftCounter from "@/components/ShiftCounter";
import NotificationBell from "@/components/NotificationBell";
import SupportWidget from "@/components/SupportWidget";
import {
  LogOut, LayoutDashboard, ShieldCheck, Crosshair, Users, Upload,
  Settings2, Inbox, ChevronLeft, ChevronRight, CalendarDays, Coffee,
  BellRing, AlertTriangle,
} from "lucide-react";

// Each nav item maps to a `page` key (matches backend PAGES list)
// plus the base roles that may see it WHEN the user has no custom-role override.
const NAV = [
  { to: "/", page: "dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", roles: ["admin", "qa"] },
  { to: "/master-exclusion", page: "master_exclusion", label: "Master Exclusion", icon: ShieldCheck, testid: "nav-master-exclusion", roles: ["admin", "qa"] },
  { to: "/campaign-exclusion", page: "campaign_exclusion", label: "Campaign Exclusion", icon: Crosshair, testid: "nav-campaign-exclusion", roles: ["admin", "qa"] },
  { to: "/leads", page: "leads", label: "Leads", icon: Users, testid: "nav-leads", roles: ["admin", "qa"] },
  { to: "/my-leads", page: null /* always */, label: "My Leads", icon: Inbox, testid: "nav-my-leads", roles: ["admin", "qa", "agent"] },
  { to: "/leads/new", page: "new_lead", label: "Add Lead", icon: Crosshair, testid: "nav-add-lead", roles: ["admin", "agent"] },
  { to: "/bulk-upload", page: "bulk_upload", label: "Bulk Upload", icon: Upload, testid: "nav-bulk-upload", roles: ["admin", "agent", "qa"] },
  { to: "/leaves", page: "leaves", label: "Leaves", icon: CalendarDays, testid: "nav-leaves", roles: ["admin", "qa", "agent"] },
  { to: "/admin", page: "admin", label: "Admin", icon: Settings2, testid: "nav-admin", roles: ["admin"] },
];

const ROLE_TONE = {
  admin: "badge-info",
  agent: "badge-neutral",
  qa: "badge-warning",
};

const SIDEBAR_KEY = "leadops.sidebar.collapsed";
// Below this viewport width the sidebar auto-collapses to icon-only and
// the toggle switches to a fixed-position overlay drawer (with backdrop)
// so it never pushes main content off-screen on phones / narrow tablets.
const NARROW_BREAKPOINT_PX = 768;

export default function Layout() {
  const { user, logout } = useAuth();
  const { status: freezeStatus, refresh: refreshFreeze } = useShiftFreeze();
  const navigate = useNavigate();
  const location = useLocation();
  const role = user?.role || "agent";
  const [pagePerms, setPagePerms] = useState(null); // null = still loading; {} = allow base
  const [breakBusy, setBreakBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === "1"; } catch { return false; }
  });
  // Track viewport width so we can switch between the desktop docked
  // sidebar and the mobile overlay drawer without a hard reload.
  const [isNarrow, setIsNarrow] = useState(() => (
    typeof window !== "undefined" ? window.innerWidth < NARROW_BREAKPOINT_PX : false
  ));
  // Mobile-only: when the user taps the toggle handle on a narrow
  // viewport, the sidebar slides in as a fixed overlay + backdrop
  // instead of eating horizontal space.
  const [mobileOpen, setMobileOpen] = useState(false);

  const takeVoluntaryBreak = async () => {
    setBreakBusy(true);
    try {
      await api.post("/shift-freeze/voluntary-break/start", { reason: "Break" });
      toast.success("Break started. App will be locked until you return.");
      await refreshFreeze();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to start break");
    } finally {
      setBreakBusy(false);
    }
  };

  const canTakeVoluntaryBreak = !!freezeStatus
    && !freezeStatus.frozen
    && freezeStatus.allow_voluntary_break === true
    && freezeStatus.reason !== "builtin_admin_bypass"
    && freezeStatus.reason !== "no_freeze_for_role";

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_KEY, collapsed ? "1" : "0"); } catch { /* ignore quota */ }
  }, [collapsed]);

  // Track viewport size — flip between docked (desktop) and drawer (mobile)
  // presentations. Debounce is unnecessary here: the state only feeds
  // className branches, not any expensive re-render.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => {
      const narrow = window.innerWidth < NARROW_BREAKPOINT_PX;
      setIsNarrow(narrow);
      // If the user resized from mobile to desktop while the drawer
      // was open, close it — the docked sidebar takes over.
      if (!narrow) setMobileOpen(false);
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Auto-close the mobile drawer on every route change so tapping a
  // nav item takes the user to the page instead of leaving the drawer
  // covering half the screen. Only `pathname` triggers this effect —
  // including `mobileOpen` in the deps would immediately re-close the
  // drawer the moment the user opens it (setMobileOpen → effect → set
  // back to false), which is exactly what happens if you're not careful.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/permissions/effective");
        if (!cancelled) setPagePerms(data.page_permissions || {});
      } catch {
        if (!cancelled) setPagePerms({}); // fallback — base-role gating only
      }
    })();
    return () => { cancelled = true; };
  }, [user?.email]);

  const items = NAV.filter((n) => {
    if (n.page === null) return true; // my-leads always
    // Special case: the Admin link is shown if the user has the full `admin`
    // page perm OR any of the admin sub-pages (campaigns / agents / users /
    // shifts / exclusion lists). This lets roles like Team Lead see the Admin
    // tab when they only have partial access.
    if (n.page === "admin" && pagePerms) {
      return pagePerms.admin === true
        || pagePerms.admin_campaigns === true
        || pagePerms.admin_agents === true
        || pagePerms.admin_users === true
        || pagePerms.admin_shifts === true
        || pagePerms.admin_exclusion_lists === true
        || pagePerms.admin_email_campaigns === true
        || pagePerms.admin_productivity === true
        || pagePerms.admin_productivity_view === true
        || pagePerms.admin_reports === true
        || pagePerms.admin_shift_schedules === true;
    }
    if (pagePerms && Object.prototype.hasOwnProperty.call(pagePerms, n.page)) {
      return pagePerms[n.page] === true;
    }
    // Fallback to base-role gating while perms load
    return n.roles.includes(role);
  });

  // ── Sidebar presentation resolution ───────────────────────────────
  // On desktop the sidebar is DOCKED (in-flow, pushes main content). On
  // narrow viewports it becomes a fixed-position DRAWER that overlays
  // content when the user opens it, and stays as a 56 px icon rail when
  // closed — so it never eats the horizontal space and never clips off
  // screen. `showLabels` controls whether the full nav labels + user
  // meta render (the drawer-when-open state always shows them regardless
  // of the persisted `collapsed` desktop preference).
  const drawerOpen = isNarrow && mobileOpen;
  const railOnly = isNarrow ? !mobileOpen : collapsed;
  const showLabels = !railOnly;
  const asideBaseCls = "border-r border-border flex flex-col transition-[width,transform] duration-200 ease-out";
  const asideCls = isNarrow
    ? (drawerOpen
      // Drawer expanded — fixed overlay, high z-index, full label width.
      ? `fixed inset-y-0 left-0 z-40 w-60 bg-background shadow-2xl ${asideBaseCls}`
      // Drawer closed — 56 px icon rail, still in flow so users see the
      // brand mark + can tap to expand.
      : `w-14 shrink-0 relative ${asideBaseCls}`)
    : `${collapsed ? "w-14" : "w-60"} shrink-0 relative ${asideBaseCls}`;

  const toggleSidebar = () => {
    if (isNarrow) setMobileOpen((o) => !o);
    else setCollapsed((c) => !c);
  };

  return (
    <div className="min-h-screen flex font-body bg-background text-foreground">
      {/* Backdrop shown behind the mobile drawer — tapping it closes. */}
      {drawerOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 z-30 bg-black/40 backdrop-blur-[1px]"
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        data-testid="app-sidebar"
        data-collapsed={railOnly ? "true" : "false"}
        data-drawer-open={drawerOpen ? "true" : "false"}
        className={asideCls}
      >
        <div className={`${railOnly ? "px-2" : "px-5"} py-6 border-b border-border`}>
          {railOnly ? (
            <div className="text-center font-display text-xl font-bold">
              E<span className="text-primary">.</span>
            </div>
          ) : (
            <>
              <div className="meta-label">Lead-Ops Suite</div>
              <h1 className="font-display text-xl font-bold mt-1">Exclusion<span className="text-primary">.</span></h1>
            </>
          )}
        </div>
        <nav className="flex-1 py-4 flex flex-col gap-1">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              title={railOnly ? n.label : undefined}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? "active" : ""} ${railOnly ? "justify-center px-0" : ""}`
              }
            >
              <n.icon size={16} strokeWidth={1.75} />
              {showLabels && <span>{n.label}</span>}
            </NavLink>
          ))}
        </nav>
        <div className={`${railOnly ? "p-2" : "p-4"} border-t border-border space-y-2`}>
          {canTakeVoluntaryBreak && (
            railOnly ? (
              <button
                data-testid="voluntary-break-btn"
                onClick={takeVoluntaryBreak}
                disabled={breakBusy}
                title="Take a break (locks app)"
                className="w-full flex justify-center p-2 rounded-sm border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800"
              >
                <Coffee size={14} />
              </button>
            ) : (
              <button
                data-testid="voluntary-break-btn"
                onClick={takeVoluntaryBreak}
                disabled={breakBusy}
                className="w-full flex items-center justify-center gap-1.5 text-[11px] py-1.5 rounded-sm border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 disabled:opacity-60"
              >
                <Coffee size={12} /> {breakBusy ? "Starting…" : "Take a break"}
              </button>
            )
          )}
          {railOnly ? (
            <button
              data-testid="logout-button"
              onClick={async () => { await logout(); navigate("/login"); }}
              className="w-full flex justify-center p-2 hover:bg-secondary rounded-sm"
              title="Sign out"
            >
              <LogOut size={14} />
            </button>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-xs font-medium truncate">{user?.name || "User"}</div>
                  <span data-testid="role-badge" className={`text-[9px] px-1.5 py-0.5 rounded-sm ${ROLE_TONE[role] || "badge-neutral"}`}>{(user?.role_label || role).toUpperCase()}</span>
                </div>
                <div className="text-[10px] font-mono text-muted-foreground truncate">{user?.email}</div>
              </div>
              <button
                data-testid="logout-button"
                onClick={async () => { await logout(); navigate("/login"); }}
                className="p-2 hover:bg-secondary rounded-sm"
                title="Sign out"
              >
                <LogOut size={14} />
              </button>
            </div>
          )}
        </div>

        {/* Edge toggle handle — sits on the sidebar's right border so the
            user can collapse/expand from either state without losing the
            anchor point. On mobile it opens/closes the drawer; on desktop
            it toggles the collapsed rail. */}
        <button
          type="button"
          data-testid="sidebar-toggle"
          aria-label={railOnly ? "Expand sidebar" : "Collapse sidebar"}
          title={railOnly ? "Expand sidebar" : "Collapse sidebar"}
          onClick={toggleSidebar}
          className="absolute -right-3 top-20 z-30 h-6 w-6 rounded-full border border-border bg-card shadow-sm flex items-center justify-center hover:bg-secondary text-foreground"
        >
          {railOnly ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>
      </aside>
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <AdminBanners pagePerms={pagePerms} baseRole={role} />
        {/* Global shift counter — mounted at the Layout level so it stays
            alive across EVERY navigation (dashboard, /leads, /my-leads,
            /leads/new, etc.). Previously it lived inside LeadForm which
            meant it unmounted the moment an agent left the Add-Lead
            page — killing the 1s tick loop that accumulates Idle/Break
            seconds and forcing a re-fetch on every remount. Backend
            `/shifts/start` is idempotent and credits any gap since
            `last_heartbeat` to idle/break appropriately, so the total
            time now stays accurate from login to logout regardless of
            which page the agent is on. Hidden on /login by the routing
            layer (Layout isn't rendered there). */}
        <div className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur px-4 py-2 flex items-center gap-3" data-testid="global-shift-counter-bar">
          <div className="flex-1 min-w-0"><ShiftCounter compact /></div>
          <NotificationBell />
        </div>
        <Outlet />
        <SupportWidget />
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AdminBanners — Persistent alerts for admin/HR/TL:
//   • Pending shift-change requests count (persistent red banner, click → Approvals)
//   • Active shift-extension reminder for the CURRENT user (their agent view)
// Poll cheap /leaves/shift-change/requests?status=pending every 60s.
// Only shows for users with `admin_leaves` permission OR base admin.
// ---------------------------------------------------------------------------
function AdminBanners({ pagePerms, baseRole }) {
  const canManageLeaves = pagePerms?.admin_leaves === true
    || pagePerms?.admin === true
    || baseRole === "admin";
  const [pendingCount, setPendingCount] = useState(0);
  const [extPendingCount, setExtPendingCount] = useState(0);
  const [extension, setExtension] = useState(null); // { end_at, minutes_added }

  useEffect(() => {
    if (!canManageLeaves) return undefined;
    let cancelled = false;
    const fetchPending = async () => {
      try {
        const r = await api.get("/leaves/shift-change/requests?status=pending");
        if (!cancelled) setPendingCount((r.data?.requests || r.data || []).length);
      } catch (e) { /* silent */ void e; }
    };
    fetchPending();
    const id = setInterval(fetchPending, 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [canManageLeaves]);

  // Pending SHIFT-EXTENSION requests — agents sit on the locked "shift ended"
  // screen waiting for approval, so poll every 30s. Endpoint needs the
  // admin_shift_schedules subpage; a 403 stops the polling for this session.
  useEffect(() => {
    let cancelled = false;
    let id = null;
    const fetchExtPending = async () => {
      try {
        const r = await api.get("/shift-freeze/extensions/pending");
        if (!cancelled) setExtPendingCount((r.data?.requests || []).length);
      } catch (e) {
        if (e.response?.status === 403 && id) { clearInterval(id); id = null; }
      }
    };
    fetchExtPending();
    id = setInterval(fetchExtPending, 30 * 1000);
    return () => { cancelled = true; if (id) clearInterval(id); };
  }, []);

  // Poll the current user's active extension (any role can have one).
  // Endpoint is optional — 404s are ignored so we don't spam warnings.
  useEffect(() => {
    let cancelled = false;
    const fetchExt = async () => {
      try {
        const r = await api.get("/shift-freeze/status");
        if (cancelled) return;
        const ext = r.data?.active_extension;
        setExtension(ext ? { end_at: ext.end_at, minutes_added: ext.minutes_added } : null);
      } catch (e) { /* silent */ void e; }
    };
    fetchExt();
    const id = setInterval(fetchExt, 2 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (pendingCount === 0 && extPendingCount === 0 && !extension) return null;
  return (
    <div className="sticky top-0 z-40" data-testid="admin-banners">
      {extPendingCount > 0 && (
        <Link
          to="/admin?tab=shift_schedules"
          data-testid="banner-pending-shift-extensions"
          className="flex items-center gap-3 px-4 py-2.5 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium border-b border-orange-700 transition-colors"
        >
          <BellRing size={16} />
          <span>
            <strong>{extPendingCount}</strong> shift extension request{extPendingCount === 1 ? "" : "s"} waiting — agent{extPendingCount === 1 ? " is" : "s are"} locked out until you approve or reject
          </span>
          <span className="ml-auto text-xs opacity-90">Review now →</span>
        </Link>
      )}
      {canManageLeaves && pendingCount > 0 && (
        <Link
          to="/leaves"
          data-testid="banner-pending-shift-requests"
          className="flex items-center gap-3 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium border-b border-red-700 transition-colors"
        >
          <BellRing size={16} />
          <span>
            <strong>{pendingCount}</strong> pending shift-change request{pendingCount === 1 ? "" : "s"} awaiting your approval
          </span>
          <span className="ml-auto text-xs opacity-90">Review now →</span>
        </Link>
      )}
      {extension && (
        <div
          data-testid="banner-shift-extension"
          className="flex items-center gap-3 px-4 py-2 bg-amber-100 text-amber-900 text-xs border-b border-amber-300"
        >
          <AlertTriangle size={14} />
          <span>
            Your shift has been extended by admin
            {extension.minutes_added ? <> — an additional <strong>{extension.minutes_added} min</strong></> : null}
            {extension.end_at ? <> until <strong>{new Date(extension.end_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</strong></> : null}.
          </span>
        </div>
      )}
    </div>
  );
}
